
import React, { useState } from 'react'
import {Plus, Package, Wrench, BarChart3, History, TrendingUp, Clock, Upload, Trash2} from 'lucide-react'
import { useInsumos } from '../hooks/useInsumos'
import InsumoForm from '../components/InsumoForm'
import ComposicaoForm from '../components/ComposicaoForm'
import HistoricoPrecos from '../components/HistoricoPrecos'
import MetricsCardInsumos from '../components/MetricsCardInsumos'
import BulkUploadInsumos from '../components/BulkUploadInsumos'
import BulkUploadComposicoes from '../components/BulkUploadComposicoes'

const BaseInsumos: React.FC = () => {
  const {
    insumos,
    composicoes,
    historicoPrecos,
    loading,
    metrics,
    createInsumo,
    updateInsumo,
    deleteInsumo,
    deleteInsumosBulk,
    createComposicao,
    updateComposicao,
    deleteComposicao,
    fetchData
  } = useInsumos()

  const [activeTab, setActiveTab] = useState('insumos')
  const [isInsumoFormOpen, setIsInsumoFormOpen] = useState(false)
  const [isComposicaoFormOpen, setIsComposicaoFormOpen] = useState(false)
  const [isHistoricoOpen, setIsHistoricoOpen] = useState(false)
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false)
  const [isBulkUploadComposicoesOpen, setIsBulkUploadComposicoesOpen] = useState(false)
  const [selectedInsumoHistorico, setSelectedInsumoHistorico] = useState<any>(null)
  const [editingInsumo, setEditingInsumo] = useState<any>(null)
  const [editingComposicao, setEditingComposicao] = useState<any>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterTipo, setFilterTipo] = useState('todos')
  
  // Estados para seleção múltipla
  const [selectedInsumos, setSelectedInsumos] = useState<string[]>([])
  const [selectMode, setSelectMode] = useState(false)

  // Verificação de segurança para arrays
  const safeInsumos = Array.isArray(insumos) ? insumos : []
  const safeComposicoes = Array.isArray(composicoes) ? composicoes : []
  const safeHistoricoPrecos = Array.isArray(historicoPrecos) ? historicoPrecos : []
  const safeMetrics = metrics || {
    totalInsumos: 0,
    valorMedioInsumos: 0,
    insumosAtivos: 0,
    produtoMaiorPreco: 'N/A'
  }

  const filteredInsumos = safeInsumos.filter(insumo => {
    const matchesSearch = insumo?.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         insumo?.tipo?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesTipo = filterTipo === 'todos' || insumo?.tipo === filterTipo
    return matchesSearch && matchesTipo
  })

  const filteredComposicoes = safeComposicoes.filter(composicao =>
    composicao?.nome?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Função para obter histórico de um insumo específico
  const getInsumoHistorico = (insumoId: string) => {
    return safeHistoricoPrecos
      .filter(h => h?.insumoId === insumoId)
      .sort((a, b) => new Date(b?.dataAlteracao || 0).getTime() - new Date(a?.dataAlteracao || 0).getTime())
  }

  // Função para obter última alteração de preço
  const getUltimaAlteracaoPreco = (insumoId: string) => {
    const historico = getInsumoHistorico(insumoId)
    return historico.length > 0 ? historico[0] : null
  }

  // Funções para seleção múltipla
  const toggleSelectMode = () => {
    setSelectMode(!selectMode)
    setSelectedInsumos([])
  }

  const toggleInsumoSelection = (insumoId: string) => {
    setSelectedInsumos(prev => 
      prev.includes(insumoId) 
        ? prev.filter(id => id !== insumoId)
        : [...prev, insumoId]
    )
  }

  const selectAllInsumos = () => {
    if (selectedInsumos.length === filteredInsumos.length) {
      setSelectedInsumos([])
    } else {
      setSelectedInsumos(filteredInsumos.map(i => i._id))
    }
  }

  const handleBulkDelete = async () => {
    if (selectedInsumos.length === 0) return
    
    const confirmMessage = `Tem certeza que deseja excluir ${selectedInsumos.length} insumo(s) selecionado(s)? Esta ação não pode ser desfeita.`
    
    if (window.confirm(confirmMessage)) {
      try {
        await deleteInsumosBulk(selectedInsumos)
        setSelectedInsumos([])
        setSelectMode(false)
      } catch (error) {
        console.error('Erro na exclusão em massa:', error)
      }
    }
  }

  const handleEditInsumo = (insumo: any) => {
    setEditingInsumo(insumo)
    setIsInsumoFormOpen(true)
  }

  const handleEditComposicao = (composicao: any) => {
    setEditingComposicao(composicao)
    setIsComposicaoFormOpen(true)
  }

  const handleCloseInsumoForm = () => {
    setIsInsumoFormOpen(false)
    setEditingInsumo(null)
  }

  const handleCloseComposicaoForm = () => {
    setIsComposicaoFormOpen(false)
    setEditingComposicao(null)
  }

  const handleInsumoSubmit = async (data: any) => {
    try {
      if (editingInsumo) {
        await updateInsumo(editingInsumo._id, data)
      } else {
        await createInsumo(data)
      }
      handleCloseInsumoForm()
    } catch (error) {
      console.error('Erro ao salvar insumo:', error)
    }
  }

  const handleComposicaoSubmit = async (data: any) => {
    try {
      if (editingComposicao) {
        await updateComposicao(editingComposicao._id, data)
      } else {
        await createComposicao(data)
      }
      handleCloseComposicaoForm()
    } catch (error) {
      console.error('Erro ao salvar composição:', error)
    }
  }

  const handleVerHistorico = (insumo: any) => {
    setSelectedInsumoHistorico(insumo)
    setIsHistoricoOpen(true)
  }

  const handleBulkUploadSuccess = () => {
    fetchData() // Recarregar dados após upload em massa
  }

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'material': return '📦'
      case 'mao_obra': return '👷'
      case 'equipamento': return '🔧'
      default: return '📋'
    }
  }

  const getTipoLabel = (tipo: string) => {
    switch (tipo) {
      case 'material': return 'Material'
      case 'mao_obra': return 'Mão de Obra'
      case 'equipamento': return 'Equipamento'
      default: return 'Outros'
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="w-full p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Base de Insumos</h1>
        <div className="flex flex-wrap gap-2">
          {activeTab === 'insumos' && (
            <>
              <button
                onClick={() => setIsBulkUploadOpen(true)}
                className="bg-green-600 text-white px-2 sm:px-4 py-2 rounded-md hover:bg-green-700 flex items-center gap-1 sm:gap-2 transition-colors text-sm sm:text-base"
              >
                <Upload size={14} className="sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Upload Insumos</span>
                <span className="sm:hidden">Upload</span>
              </button>
              <button
                onClick={toggleSelectMode}
                className={`px-2 sm:px-4 py-2 rounded-md flex items-center gap-1 sm:gap-2 transition-colors text-sm sm:text-base ${
                  selectMode 
                    ? 'bg-gray-600 text-white hover:bg-gray-700' 
                    : 'bg-orange-600 text-white hover:bg-orange-700'
                }`}
              >
                <Trash2 size={14} className="sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">
                  {selectMode ? 'Cancelar Seleção' : 'Excluir Múltiplos'}
                </span>
                <span className="sm:hidden">
                  {selectMode ? 'Cancelar' : 'Excluir'}
                </span>
              </button>
            </>
          )}
          {activeTab === 'composicoes' && (
            <button
              onClick={() => setIsBulkUploadComposicoesOpen(true)}
              className="bg-green-600 text-white px-2 sm:px-4 py-2 rounded-md hover:bg-green-700 flex items-center gap-1 sm:gap-2 transition-colors text-sm sm:text-base"
            >
              <Upload size={14} className="sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Upload Composições</span>
              <span className="sm:hidden">Upload</span>
            </button>
          )}
          <button
            onClick={() => {
              setSelectedInsumoHistorico(null)
              setIsHistoricoOpen(true)
            }}
            className="bg-purple-600 text-white px-2 sm:px-4 py-2 rounded-md hover:bg-purple-700 flex items-center gap-1 sm:gap-2 transition-colors text-sm sm:text-base"
          >
            <History size={14} className="sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Histórico Geral</span>
            <span className="sm:hidden">Histórico</span>
          </button>
        </div>
      </div>

      {/* Barra de ações para exclusão em massa */}
      {selectMode && activeTab === 'insumos' && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={selectAllInsumos}
                className="text-sm text-orange-600 hover:text-orange-800 font-medium"
              >
                {selectedInsumos.length === filteredInsumos.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
              </button>
              <span className="text-sm text-gray-600">
                {selectedInsumos.length} de {filteredInsumos.length} selecionado(s)
              </span>
            </div>
            {selectedInsumos.length > 0 && (
              <button
                onClick={handleBulkDelete}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 flex items-center gap-2 transition-colors text-sm"
              >
                <Trash2 size={16} />
                Excluir {selectedInsumos.length} Selecionado(s)
              </button>
            )}
          </div>
        </div>
      )}

      {/* Métricas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-4 sm:mb-6">
        <MetricsCardInsumos
          title="Total de Insumos"
          value={safeMetrics.totalInsumos}
          icon={Package}
          color="blue"
        />
        <MetricsCardInsumos
          title="Preço Médio"
          value={`R$ ${(safeMetrics.valorMedioInsumos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          icon={TrendingUp}
          color="green"
        />
        <MetricsCardInsumos
          title="Insumos Ativos"
          value={safeMetrics.insumosAtivos}
          icon={BarChart3}
          color="purple"
        />
        <MetricsCardInsumos
          title="Maior Preço"
          value={safeMetrics.produtoMaiorPreco}
          icon={Package}
          color="orange"
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4 sm:mb-6 overflow-x-auto">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 whitespace-nowrap">
          <button
            onClick={() => setActiveTab('insumos')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'insumos'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Package className="w-4 h-4 inline mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Insumos ({safeInsumos.length})</span>
            <span className="sm:hidden">Insumos</span>
          </button>
          <button
            onClick={() => setActiveTab('composicoes')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'composicoes'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Wrench className="w-4 h-4 inline mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Composições ({safeComposicoes.length})</span>
            <span className="sm:hidden">Composições</span>
          </button>
        </nav>
      </div>

      {/* Filtros e Busca */}
      <div className="flex flex-col sm:flex-row gap-4 mb-4 sm:mb-6 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
          />
        </div>
        
        {activeTab === 'insumos' && (
          <select
            value={filterTipo}
            onChange={(e) => setFilterTipo(e.target.value)}
            className="p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[150px] text-sm sm:text-base"
          >
            <option value="todos">Todos os tipos</option>
            <option value="material">Material</option>
            <option value="mao_obra">Mão de Obra</option>
            <option value="equipamento">Equipamento</option>
          </select>
        )}

        <button
          onClick={() => {
            if (activeTab === 'insumos') {
              setIsInsumoFormOpen(true)
            } else if (activeTab === 'composicoes') {
              setIsComposicaoFormOpen(true)
            }
          }}
          className="bg-blue-600 text-white px-2 sm:px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-1 sm:gap-2 whitespace-nowrap transition-colors text-sm sm:text-base min-w-[150px] justify-center"
        >
          <Plus size={14} className="sm:w-4 sm:h-4" />
          {activeTab === 'insumos' ? (
            <>
              <span className="hidden sm:inline">Novo Insumo</span>
              <span className="sm:hidden">Novo</span>
            </>
          ) : (
            <>
              <span className="hidden sm:inline">Nova Composição</span>
              <span className="sm:hidden">Nova</span>
            </>
          )}
        </button>
      </div>

      {/* Conteúdo das Tabs */}
      {activeTab === 'insumos' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-auto divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {selectMode && (
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedInsumos.length === filteredInsumos.length && filteredInsumos.length > 0}
                        onChange={selectAllInsumos}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                  )}
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Insumo
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Preço Unit.
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInsumos.map((insumo) => {
                  const ultimaAlteracao = getUltimaAlteracaoPreco(insumo._id)
                  const historicoCount = getInsumoHistorico(insumo._id).length
                  
                  return (
                    <tr key={insumo._id} className="hover:bg-gray-50">
                      {selectMode && (
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedInsumos.includes(insumo._id)}
                            onChange={() => toggleInsumoSelection(insumo._id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                      )}
                      <td className="px-3 sm:px-6 py-4">
                        <div className="flex items-center">
                          <span className="text-lg sm:text-2xl mr-2 sm:mr-3">{getTipoIcon(insumo.tipo)}</span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 whitespace-normal break-words">
                              {insumo.nome}
                            </div>
                            <div className="text-sm text-gray-500 whitespace-normal break-words">
                              {insumo.unidadeMedida}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {getTipoLabel(insumo.tipo)}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">
                            R$ {(insumo.precoUnitario || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </div>
                          {ultimaAlteracao && (
                            <div className="text-xs text-gray-500 whitespace-normal break-words">
                              <span className={`font-medium ${
                                ultimaAlteracao.percentualVariacao > 0 ? 'text-red-600' : 'text-green-600'
                              }`}>
                                {ultimaAlteracao.percentualVariacao > 0 ? '+' : ''}{ultimaAlteracao.percentualVariacao.toFixed(1)}%
                              </span>
                              {' em ' + formatDate(ultimaAlteracao.dataAlteracao)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          insumo.ativo 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {insumo.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {!selectMode && (
                          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-1 sm:space-y-0 sm:space-x-2">
                            <button
                              onClick={() => handleVerHistorico(insumo)}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 transition-colors"
                              title="Ver histórico de preços"
                            >
                              <Clock size={12} />
                              <span className="hidden sm:inline">
                                {historicoCount > 0 ? `${historicoCount}` : 'Histórico'}
                              </span>
                              <span className="sm:hidden">
                                {historicoCount > 0 ? `${historicoCount}` : 'Hist'}
                              </span>
                            </button>
                            <button
                              onClick={() => handleEditInsumo(insumo)}
                              className="text-blue-600 hover:text-blue-900 text-xs sm:text-sm"
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => deleteInsumo(insumo._id)}
                              className="text-red-600 hover:text-red-900 text-xs sm:text-sm"
                            >
                              Excluir
                            </button>
                          </div>
                        )}
                        {selectMode && (
                          <span className="text-gray-400 text-xs">Modo seleção ativo</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'composicoes' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-auto divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Composição
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Itens
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor Total
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredComposicoes.map((composicao) => (
                  <tr key={composicao._id} className="hover:bg-gray-50">
                    <td className="px-3 sm:px-6 py-4">
                      <div className="flex items-center">
                        <span className="text-lg sm:text-2xl mr-2 sm:mr-3">🔧</span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 whitespace-normal break-words">
                            {composicao.nome}
                          </div>
                          <div className="text-sm text-gray-500 whitespace-normal break-words">
                            {composicao.unidadeMedida}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {composicao.itens?.length || 0} itens
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      R$ {(composicao.valorTotal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        composicao.ativo 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {composicao.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:space-x-2">
                        <button
                          onClick={() => handleEditComposicao(composicao)}
                          className="text-blue-600 hover:text-blue-900 text-xs sm:text-sm"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => deleteComposicao(composicao._id)}
                          className="text-red-600 hover:text-red-900 text-xs sm:text-sm"
                        >
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Formulários */}
      <InsumoForm
        isOpen={isInsumoFormOpen}
        onClose={handleCloseInsumoForm}
        onSubmit={handleInsumoSubmit}
        initialData={editingInsumo}
        title={editingInsumo ? 'Editar Insumo' : 'Novo Insumo'}
      />

      <ComposicaoForm
        isOpen={isComposicaoFormOpen}
        onClose={handleCloseComposicaoForm}
        onSubmit={handleComposicaoSubmit}
        insumos={safeInsumos}
        composicoes={safeComposicoes}
        initialData={editingComposicao}
        title={editingComposicao ? 'Editar Composição' : 'Nova Composição'}
      />

      <HistoricoPrecos
        isOpen={isHistoricoOpen}
        onClose={() => setIsHistoricoOpen(false)}
        insumo={selectedInsumoHistorico}
        historico={safeHistoricoPrecos}
      />

      <BulkUploadInsumos
        isOpen={isBulkUploadOpen}
        onClose={() => setIsBulkUploadOpen(false)}
        onSuccess={handleBulkUploadSuccess}
      />

      <BulkUploadComposicoes
        isOpen={isBulkUploadComposicoesOpen}
        onClose={() => setIsBulkUploadComposicoesOpen(false)}
        onSuccess={handleBulkUploadSuccess}
        insumos={safeInsumos}
        composicoes={safeComposicoes}
      />
    </div>
  )
}

export default BaseInsumos
